import React from 'react';
import { Truck } from 'lucide-react';
import { formatMoney } from '../../utils/format';

/**
 * FleetReportBlock — 22/07/2026 (retour user). Rapport Flotte annuel signé
 * Rémi Forni au Conseil : usage et rentabilité par camion + typologie des
 * contrats acceptés. Null si Forni non recruté (annualReport.fleetReport
 * absent) ou flotte vide.
 */
export default function FleetReportBlock({ report }) {
  if (!report || !report.rows || report.rows.length === 0) return null;
  const { rows, totals } = report;
  return (
    <div className="border-t border-ink-600 pt-4" data-testid="fleet-report-block">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded bg-sky-900/30">
          <Truck className="w-4 h-4 text-sky-400" />
        </div>
        <div>
          <h4 className="font-bold text-sm text-slate-200">Rapport Flotte — Rémi Forni</h4>
          <p className="text-[10px] text-slate-500">Usage et rentabilité par camion (CA attribué − carburant − entretien/assurance/financement)</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] text-slate-500 uppercase">
              <th className="text-left py-1 pr-2">Camion</th>
              <th className="text-right py-1 px-2">CA</th>
              <th className="text-right py-1 px-2">Actif / immo.</th>
              <th className="text-right py-1 px-2">Coûts</th>
              <th className="text-right py-1 px-2">Résultat</th>
              <th className="text-right py-1 pl-2">Marge</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <React.Fragment key={r.id}>
                <tr className="border-t border-ink-600/60" data-testid={`fleet-row-${r.id}`}>
                  <td className="py-1.5 pr-2 text-slate-200 font-semibold">
                    {r.label}{r.isLeased && <span className="text-[9px] text-slate-500 ml-1">(leasing)</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-slate-300">{formatMoney(r.revenue)}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-slate-400">
                    {r.activeDays} j{r.downDays > 0 && <span className="text-danger"> / {r.downDays} j</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-slate-400">{formatMoney(r.fuelCost + r.fixedCost)}</td>
                  <td className={`py-1.5 px-2 text-right font-mono font-bold ${r.profit >= 0 ? 'text-money' : 'text-danger'}`}>
                    {r.profit >= 0 ? '+' : ''}{formatMoney(r.profit)}
                  </td>
                  <td className={`py-1.5 pl-2 text-right font-mono ${r.marginPct === null ? 'text-slate-600' : r.marginPct >= 20 ? 'text-money' : r.marginPct >= 0 ? 'text-warn' : 'text-danger'}`}>
                    {r.marginPct === null ? '—' : `${r.marginPct} %`}
                  </td>
                </tr>
                {Object.keys(r.contractTypes || {}).length > 0 && (
                  <tr>
                    <td colSpan={6} className="pb-1.5 pr-2">
                      <div className="flex flex-wrap gap-1" data-testid={`fleet-types-${r.id}`}>
                        {Object.entries(r.contractTypes).sort((a, b) => b[1] - a[1]).map(([cat, n]) => (
                          <span key={cat} className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/20 font-mono">
                            {cat} ×{n}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-ink-500">
              <td className="py-1.5 pr-2 text-slate-400 font-bold uppercase text-[10px]">Total flotte</td>
              <td className="py-1.5 px-2 text-right font-mono text-slate-200 font-bold">{formatMoney(totals.revenue)}</td>
              <td />
              <td className="py-1.5 px-2 text-right font-mono text-slate-300">{formatMoney(totals.fuelCost + totals.fixedCost)}</td>
              <td className={`py-1.5 px-2 text-right font-mono font-black ${totals.profit >= 0 ? 'text-money' : 'text-danger'}`}>
                {totals.profit >= 0 ? '+' : ''}{formatMoney(totals.profit)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[9px] text-slate-600 italic mt-1.5">
        Coûts = carburant réel + entretien, assurance et loyer/mensualité au prorata. Hors bonus globaux (marketing, boom sectoriel).
      </p>
    </div>
  );
}
